import { BufferGeometry, Material } from "three";

// ===================== 基础类型定义 =====================

/**
 * 处理后的3D模型数据
 */
export interface ProcessedModel {
  /** 处理后的几何体 */
  geometry: BufferGeometry;
  /** 原始材质 */
  material: Material | Material[];
  /** 模型名称 */
  name: string;
}

/**
 * 点云配置参数
 */
export interface PointCloudConfig {
  /** 是否显示点云 */
  showPointCloud: boolean;
  /** 点的世界尺寸 */
  pointSize: number;
  /** 总点数 */
  pointCount: number;
  /** 尺寸倍数 */
  sizeMultiplier: number;
  /** 透明度 */
  opacity: number;
}

/**
 * 水晶模型类型
 */
export type CrystalModelType =
  | "cube"
  | "crystal_1"
  | "crystal_2"
  | "crystal_3"
  | "crystal_4";

/**
 * 焦散效果配置
 * 注意: 光源位置和焦散平面位置现在由 SceneOpticsConfig 统一管理
 */
export interface CausticConfig {
  /** 是否启用焦散效果 */
  enabled: boolean;
  /** 是否仅显示焦散效果 */
  causticsOnly: boolean;
  /** 是否渲染背面 */
  backside: boolean;
  /** 焦散颜色 */
  color: string;
  /** 世界半径 */
  worldRadius: number;
  /** 折射率 */
  ior: number;
  /** 背面折射率 */
  backsideIOR: number;
  /** 强度 */
  intensity: number;
}

/**
 * 阴影效果配置
 * 注意: 光源位置和阴影平面位置现在由 SceneOpticsConfig 统一管理
 */
export interface ShadowConfig {
  /** 是否启用阴影 */
  enabled: boolean;
  /** 延迟初始化时间（毫秒） */
  initializationDelay: number;
  /** AccumulativeShadows 配置 */
  accumulative: {
    frames: number;
    color: string;
    colorBlend: number;
    toneMapped: boolean;
    alphaTest: number;
    opacity: number;
    scale: number;
  };
  /** RandomizedLight 配置 */
  randomizedLight: {
    amount: number;
    radius: number;
    ambient: number;
    bias: number;
  };
}

/**
 * 场景光学效果统一配置
 * 管理 Shadow 和 Caustic 系统共享的物理参数
 */
export interface SceneOpticsConfig {
  /** 是否启用场景级光学效果 */
  enabled: boolean;
  /** 光源位置 - Shadow 和 Caustic 共享 */
  lightSource: [number, number, number];
  /** 效果平面位置 - Shadow 和 Caustic 投射平面 */
  effectPlane: [number, number, number];
}

/**
 * 水晶外壳配置
 */
export interface CrystalShellConfig {
  /** 透射率 */
  transmission: number;
  /** 粗糙度 */
  roughness: number;
  /** 折射率 */
  ior: number;
  /** 色差 */
  chromaticAberration: number;
  /** 扭曲度 */
  distortion: number;
  /** 各向异性 */
  anisotropy: number;
  /** 颜色 */
  color: string;
  /** 尺寸（几何体大小） */
  size: number;
  /** 厚度（材质光学厚度） */
  thickness: number;
  /** 模型类型 */
  modelType: CrystalModelType;
}

/**
 * 激光物理参数配置
 */
export interface LaserPhysicsConfig {
  /** 激光烧蚀反射率 */
  laserAblationReflectance: number;
  /** 比尔-朗伯衰减系数 */
  depthAttenuationFactor: number;
  /** 分层数量 */
  layers: number;
  /** 亮度梯度增强系数 */
  brightnessGradient: number;
  /** 激光能量光谱RGB配比 */
  spectrumRGB: {
    red: number;
    green: number;
    blue: number;
  };
}

/**
 * 内部模型处理器配置
 */
export interface InnerModelProcessorConfig {
  /** 目标尺寸比例（相对于水晶尺寸） */
  targetSizeRatio: number;
  /** 是否自动居中 */
  autoCenter: boolean;
  /** 旋转角度（X轴） */
  rotationX: number;
}

// ===================== Hook 返回类型 =====================

/**
 * 内部模型处理器返回结果
 */
export interface InnerModelProcessorResult {
  /** 处理后的模型数组 */
  processedModels: ProcessedModel[];
  /** 源几何体（用于点云生成） */
  sourceGeometry: BufferGeometry | null;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
}

/**
 * 点云生成器返回结果
 */
export interface PointCloudGeneratorResult {
  /** 点云几何体 */
  pointGeometry: BufferGeometry | null;
  /** 是否正在生成 */
  isGenerating: boolean;
  /** 是否完全初始化（几何体属性已同步到GPU） */
  isFullyInitialized: boolean;
  /** 错误信息 */
  error: string | null;
  /** 生成的点数 */
  generatedPointCount: number;
}

/**
 * 水晶外壳Hook返回结果
 */
export interface CrystalShellResult {
  /** 外壳配置 */
  config: CrystalShellConfig;
  /** 重置为默认值 */
  resetToDefaults: () => void;
}

/**
 * 阴影系统Hook返回结果
 */
export interface ShadowSystemResult {
  /** 阴影效果配置 */
  config: ShadowConfig;
  /** 阴影是否准备就绪 */
  shadowsReady: boolean;
  /** 手动重置阴影 */
  resetShadows: () => void;
  /** 重置为默认值 */
  resetToDefaults: () => void;
}

/**
 * 焦散效果Hook返回结果
 */
export interface CausticSystemResult {
  /** 焦散效果配置 */
  config: CausticConfig;
  /** 重置为默认值 */
  resetToDefaults: () => void;
}

/**
 * 场景光学系统Hook返回结果
 */
export interface SceneOpticsSystemResult {
  /** 场景光学效果配置 */
  config: SceneOpticsConfig;
  /** 重置为默认值 */
  resetToDefaults: () => void;
}

// ===================== 组件Props类型 =====================

/**
 * 水晶容器组件属性
 */
export interface CrystalContainerProps {
  /** 位置 */
  position?: [number, number, number];
  /** 旋转 */
  rotation?: [number, number, number];
  /** 缩放 */
  scale?: number | [number, number, number];
  /** 外壳配置 */
  shellConfig: CrystalShellConfig;
  /** 是否投射阴影 */
  castShadow?: boolean;
  /** 是否接收阴影 */
  receiveShadow?: boolean;
}

/**
 * 水晶模型组件属性
 */
export interface CrystalModelProps {
  /** 模型文件路径 */
  modelPath: string;
  /** 位置 */
  position?: [number, number, number];
  /** 旋转 */
  rotation?: [number, number, number];
  /** 缩放 */
  scale?: number | [number, number, number];
  /** 外壳配置 */
  shellConfig: CrystalShellConfig;
  /** 是否投射阴影 */
  castShadow?: boolean;
  /** 是否接收阴影 */
  receiveShadow?: boolean;
}

/**
 * 点云组件属性
 */
export interface PointCloudProps {
  /** 点云几何体 */
  geometry: BufferGeometry | null;
  /** 点云配置 */
  config: PointCloudConfig;
  /** 是否可见 */
  visible?: boolean;
}

/**
 * IronMan模型组件属性
 */
export interface IronManModelProps {
  /** 处理后的模型数据 */
  models: ProcessedModel[];
  /** 位置 */
  position?: [number, number, number];
  /** 是否可见 */
  visible?: boolean;
}

/**
 * 激光内雕组合组件属性（重构后：纯渲染组件）
 */
export interface LaserEngravingProps {
  /** 位置 */
  position?: [number, number, number];
  /** 旋转 */
  rotation?: [number, number, number];
  /** 缩放 */
  scale?: number | [number, number, number];
  /** 显示模式：点云或模型 */
  displayMode: "pointCloud" | "model";
  /** 点云配置 */
  pointCloudConfig: PointCloudConfig;
  /** 水晶外壳配置 */
  crystalConfig: CrystalShellConfig;
  /** 点云几何体（必需，由页面层面提供） */
  pointGeometry: BufferGeometry | null;
  /** 处理后的模型数据（必需，由页面层面提供） */
  processedModels: ProcessedModel[];
  /** 加载状态（可选，用于显示加载指示器） */
  isLoading?: boolean;
  /** 错误信息（可选，用于错误处理） */
  error?: string | null;
}

// ===================== 常量类型 =====================

/**
 * 显示模式枚举
 */
export enum DisplayMode {
  POINT_CLOUD = "pointCloud",
  MODEL = "model",
}

// ===================== 工具类型 =====================
